import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  CheckCircle2Icon,
  FileTextIcon,
  PrinterIcon,
  SendIcon,
  UndoDotIcon,
  UsersIcon,
  XCircleIcon
} from 'lucide-react';
import { AppBar } from '../../components/AppBar';
import { ProposalReadView } from '../../components/ProposalReadView';
import {
  ObservationLetterField,
  observationLetterProblem
} from '../../components/committee/ObservationLetterField';
import { usePortal } from '../../contexts/PortalContext';
import * as api from '../../api/research';
import type {
  CommitteeProposalDetail
} from '../../api/research';
import type {
  ReviewSummaryAnswerRow,
  ReviewSummaryCommentRow,
  ReviewSummaryReviewerRow,
  ReviewerRow
} from '../../api/types';
import { formatDateTime } from '../../data/fieldMap';

type Panel = 'proposal' | 'reviews' | 'assign';

export function CommitteeProposal() {
  const { researchCode: codeParam } = useParams();
  const researchCode = Number(codeParam);
  const { sessionToken } = usePortal();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<CommitteeProposalDetail | null>(null);
  const [panel, setPanel] = useState<Panel>('proposal');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [remarks, setRemarks] = useState('');
  const [letter, setLetter] = useState<File | null>(null);
  const [letterKey, setLetterKey] = useState(0);
  const [reviewers, setReviewers] = useState<ReviewerRow[]>([]);
  const [picked, setPicked] = useState<number[]>([]);
  const [dueDate, setDueDate] = useState('');

  const [summary, setSummary] = useState<{
    reviewers: ReviewSummaryReviewerRow[];
    answers: ReviewSummaryAnswerRow[];
    comments: ReviewSummaryCommentRow[];
  } | null>(null);

  const load = useCallback(async () => {
    if (!sessionToken || !researchCode) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.committeeProposal(sessionToken, researchCode);
      if (!res.ok) setError(res.message);
      else setDetail(res.detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the proposal.');
    } finally {
      setLoading(false);
    }
  }, [researchCode, sessionToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadReviewers = useCallback(async () => {
    if (!sessionToken) return;
    const res = await api.listReviewers(sessionToken, researchCode);
    if (res.ok) {
      setReviewers(res.rows);
      setPicked(res.rows.filter((r) => r.IsAssigned).map((r) => r.ReviewerId));
    } else {
      setError(res.message);
    }
  }, [researchCode, sessionToken]);

  const loadSummary = useCallback(async () => {
    if (!sessionToken) return;
    const res = await api.reviewSummary(sessionToken, researchCode);
    if (res.ok) {
      setSummary({ reviewers: res.reviewers, answers: res.answers, comments: res.comments });
    } else {
      setError(res.message);
    }
  }, [researchCode, sessionToken]);

  useEffect(() => {
    if (panel === 'assign') void loadReviewers();
    if (panel === 'reviews') void loadSummary();
  }, [loadReviewers, loadSummary, panel]);

  const act = async (fn: () => Promise<{ ok: boolean; message: string }>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fn();
      if (res.ok) {
        setNotice(res.message);
        setRemarks('');
        setLetter(null);
        setLetterKey((k) => k + 1);
        await load();
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That action could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  if (!sessionToken) return null;

  const header = detail?.header;
  const status = header?.StatusId ?? '';
  const canForward =
    (detail?.isIrbAdmin && ['04', '06', '08'].includes(status)) ||
    (detail?.isSrcAdmin && status === '02');
  const canSendBack =
    (detail?.isIrbAdmin && ['04', '05', '06', '08', '09'].includes(status)) ||
    (detail?.isSrcAdmin && status === '02');
  const canDecide = detail?.isIrbAdmin && ['04', '06', '08', '09'].includes(status);
  const canAssign = detail?.isIrbAdmin && ['04', '05', '06', '08'].includes(status);

  return (
    <div className="page">
      <AppBar showHome={false} />
      <main className="container section-gap">
        <div className="stack">
          <button type="button" className="link-button" onClick={() => navigate('/committee')}>
            <ArrowLeftIcon size={14} aria-hidden="true" /> Back to the committee desk
          </button>

          {error ? (
            <div className="notice notice--error" role="alert">
              <AlertCircleIcon size={18} aria-hidden="true" />
              <span>{error}</span>
            </div>
          ) : null}

          {notice ? (
            <div className="notice" role="status">
              <CheckCircle2Icon size={18} aria-hidden="true" />
              <span>{notice}</span>
            </div>
          ) : null}

          {loading ? (
            <p className="table-empty">Loading the proposal…</p>
          ) : !detail || !header ? (
            <p className="table-empty">That proposal is not available to you.</p>
          ) : (
            <>
              <section className="hero">
                <div>
                  <h1>{detail.body?.Title || 'Untitled proposal'}</h1>
                  <p>
                    {header.IrbNumber ?? `#${header.ResearchCode}`} ·{' '}
                    {detail.researcherName} &lt;{detail.researcherEmail}&gt; · submitted{' '}
                    {formatDateTime(header.SubmitDateTime)}
                    {header.Revisions > 0 ? ` · revision ${header.Revisions}` : ''}
                  </p>
                </div>
                <div className="inline-actions" style={{ alignItems: 'center' }}>
                  {header.FileName ? (
                    <a
                      className="btn btn--ghost"
                      href={api.submissionPdfUrl(sessionToken, researchCode)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FileTextIcon size={16} aria-hidden="true" />
                      Submitted document
                    </a>
                  ) : null}
                  <span className="badge badge--blue">{header.StatusDescription}</span>
                </div>
              </section>

              <nav className="tab-row" aria-label="Proposal sections">
                <button
                  type="button"
                  className={`tab${panel === 'proposal' ? ' tab--active' : ''}`}
                  onClick={() => setPanel('proposal')}
                >
                  Proposal
                </button>
                <button
                  type="button"
                  className={`tab${panel === 'reviews' ? ' tab--active' : ''}`}
                  onClick={() => setPanel('reviews')}
                >
                  Reviews ({detail.assignments.filter((a) => a.IsReviewed).length}/
                  {detail.assignments.length})
                </button>
                {canAssign ? (
                  <button
                    type="button"
                    className={`tab${panel === 'assign' ? ' tab--active' : ''}`}
                    onClick={() => setPanel('assign')}
                  >
                    Assign reviewers
                  </button>
                ) : null}
              </nav>

              {panel === 'proposal' ? (
                <ProposalReadView
                  body={detail.body}
                  typeNames={detail.typeNames}
                  gantt={detail.gantt}
                  submission={detail.submission}
                  coInvestigators={detail.coInvestigators}
                  documents={detail.documents}
                  sessionToken={sessionToken}
                  researchCode={researchCode}
                />
              ) : null}

              {panel === 'reviews' ? (
                <ReviewsPanel detail={detail} summary={summary} sessionToken={sessionToken} />
              ) : null}

              {panel === 'assign' ? (
                <section className="card">
                  <div className="card-header">
                    <div>
                      <h2 className="card-title">Assign reviewers</h2>
                      <p className="card-subtitle">
                        Everyone you tick is emailed the proposal. Reviewers already
                        working on it stay assigned.
                      </p>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="table-scroll">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th scope="col" className="col-select">
                              Select
                            </th>
                            <th scope="col">Reviewer</th>
                            <th scope="col">Email</th>
                            <th scope="col">Open reviews</th>
                            <th scope="col">Already assigned</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reviewers.length === 0 ? (
                            <tr>
                              <td colSpan={5}>
                                <p className="table-empty">No reviewer accounts found.</p>
                              </td>
                            </tr>
                          ) : (
                            reviewers.map((r) => (
                              <tr key={r.ReviewerId}>
                                <td className="col-select">
                                  <input
                                    type="checkbox"
                                    aria-label={`Assign ${r.Name}`}
                                    checked={picked.includes(r.ReviewerId)}
                                    disabled={r.IsAssigned}
                                    onChange={() =>
                                      setPicked((prev) =>
                                        prev.includes(r.ReviewerId)
                                          ? prev.filter((id) => id !== r.ReviewerId)
                                          : [...prev, r.ReviewerId]
                                      )
                                    }
                                    style={{ width: 16, height: 16, accentColor: 'var(--blue-700)' }}
                                  />
                                </td>
                                <td>{r.Name}</td>
                                <td className="muted">{r.Email}</td>
                                <td>{r.OpenReviews}</td>
                                <td>{r.IsAssigned ? 'Yes' : '—'}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="grid-2" style={{ marginTop: 'var(--space-4)', maxWidth: 420 }}>
                      <div className="field">
                        <label className="field-label" htmlFor="due-date">
                          Reviews due by
                        </label>
                        <input
                          id="due-date"
                          className="input"
                          type="date"
                          value={dueDate}
                          onChange={(e) => setDueDate(e.target.value)}
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn btn--primary"
                      style={{ marginTop: 'var(--space-4)' }}
                      disabled={busy || picked.length === 0}
                      onClick={() =>
                        void act(async () => {
                          const res = await api.assignReviewers({
                            SessionToken: sessionToken,
                            ResearchCode: researchCode,
                            ReviewerIds: JSON.stringify(picked),
                            DueDate: dueDate
                          });
                          if (res.ok) await loadReviewers();
                          return res;
                        })
                      }
                    >
                      <UsersIcon size={16} aria-hidden="true" />
                      {busy ? 'Assigning…' : 'Assign selected reviewers'}
                    </button>
                  </div>
                </section>
              ) : null}

              <section className="card">
                <div className="card-header">
                  <div>
                    <h2 className="card-title">Decision</h2>
                    <p className="card-subtitle">
                      Remarks are emailed to the researcher and recorded in the proposal's
                      history.
                    </p>
                  </div>
                </div>
                <div className="card-body">
                  <div className="field">
                    <label className="field-label" htmlFor="remarks">
                      Remarks
                    </label>
                    <textarea
                      id="remarks"
                      className="textarea"
                      rows={5}
                      placeholder="What the researcher needs to know…"
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                    />
                  </div>

                  {canSendBack ? (
                    <ObservationLetterField
                      id="observation-letter"
                      file={letter}
                      onChange={setLetter}
                      resetKey={letterKey}
                    />
                  ) : null}

                  <div className="inline-actions" style={{ marginTop: 'var(--space-4)', flexWrap: 'wrap' }}>
                    {canForward ? (
                      <button
                        type="button"
                        className="btn btn--primary"
                        disabled={busy}
                        onClick={() =>
                          void act(() =>
                            api.forwardProposal({
                              SessionToken: sessionToken,
                              ResearchCode: researchCode,
                              Remarks: remarks
                            })
                          )
                        }
                      >
                        <SendIcon size={16} aria-hidden="true" />
                        Forward
                      </button>
                    ) : null}

                    {canSendBack ? (
                      <button
                        type="button"
                        className="btn btn--secondary"
                        disabled={busy}
                        onClick={() =>
                          void act(async () => {
                            const problem = !remarks.trim()
                              ? 'Please enter the corrections.'
                              : observationLetterProblem(letter);
                            if (problem || !letter) return { ok: false, message: problem ?? '' };
                            return api.sendBackProposal({
                              SessionToken: sessionToken,
                              ResearchCode: researchCode,
                              Remarks: remarks,
                              ObservationLetter: letter
                            });
                          })
                        }
                      >
                        <UndoDotIcon size={16} aria-hidden="true" />
                        Send back for correction
                      </button>
                    ) : null}

                    {canDecide ? (
                      <>
                        <button
                          type="button"
                          className="btn btn--success"
                          disabled={busy}
                          onClick={() =>
                            void act(() =>
                              api.decideProposal({
                                SessionToken: sessionToken,
                                ResearchCode: researchCode,
                                Approved: true,
                                Remarks: remarks
                              })
                            )
                          }
                        >
                          <CheckCircle2Icon size={16} aria-hidden="true" />
                          Approve
                        </button>
                        <button
                          type="button"
                          className="btn btn--danger"
                          disabled={busy}
                          onClick={() =>
                            void act(() =>
                              api.decideProposal({
                                SessionToken: sessionToken,
                                ResearchCode: researchCode,
                                Approved: false,
                                Remarks: remarks
                              })
                            )
                          }
                        >
                          <XCircleIcon size={16} aria-hidden="true" />
                          Do not approve
                        </button>
                      </>
                    ) : null}

                    {!canForward && !canSendBack && !canDecide ? (
                      <p className="muted">
                        No action is open to your office at this stage.
                      </p>
                    ) : null}
                  </div>
                </div>
              </section>

              <section className="card">
                <div className="card-header">
                  <h2 className="card-title">History</h2>
                </div>
                <div className="card-body">
                  <ul className="history-list">
                    {detail.history.map((row, index) => (
                      <li key={`${row.StatusCode}-${index}`}>
                        <span className="history-when">{formatDateTime(row.ActionedOn)}</span>
                        <span className="history-what">
                          <strong>{row.StatusDescription || row.StatusCode}</strong>
                          {row.ActionedBy ? ` · ${row.ActionedBy}` : ''}
                          {row.Remarks?.trim() ? (
                            <span className="history-remark">{row.Remarks}</span>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function ReviewsPanel({
  detail,
  summary,
  sessionToken
}: {
  detail: CommitteeProposalDetail;
  summary: {
    reviewers: ReviewSummaryReviewerRow[];
    answers: ReviewSummaryAnswerRow[];
    comments: ReviewSummaryCommentRow[];
  } | null;
  sessionToken: string;
}) {
  if (detail.assignments.length === 0) {
    return (
      <section className="card">
        <div className="card-body">
          <p className="table-empty">No reviewers have been assigned yet.</p>
        </div>
      </section>
    );
  }

  const done = summary?.reviewers.filter((r) => r.IsReviewed) ?? [];
  const sections = Array.from(new Set((summary?.answers ?? []).map((a) => a.SectionName)));

  return (
    <div className="stack">
      <section className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Reviewer progress</h2>
            <p className="card-subtitle">
              The printable report covers every completed review, question by
              question, with the written comments after.
            </p>
          </div>
          {done.length > 0 ? (
            <a
              className="btn btn--secondary btn--small"
              href={api.reviewReportUrl(sessionToken, detail.header?.ResearchCode ?? 0)}
              target="_blank"
              rel="noreferrer"
            >
              <PrinterIcon size={14} aria-hidden="true" />
              Print review report
            </a>
          ) : null}
        </div>
        <div className="card-body">
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Reviewer</th>
                  <th scope="col">Status</th>
                  <th scope="col">Due</th>
                  <th scope="col">Completed</th>
                </tr>
              </thead>
              <tbody>
                {detail.assignments.map((a) => (
                  <tr key={a.ReviewerId}>
                    <td>
                      {a.ReviewerName}
                      <br />
                      <span className="muted">{a.ReviewerEmail}</span>
                    </td>
                    <td>
                      <span className={`badge ${a.IsReviewed ? 'badge--green' : 'badge--grey'}`}>
                        {a.IsReviewed ? 'Reviewed' : 'Pending'}
                      </span>
                    </td>
                    <td className="muted">{a.DueDate || '—'}</td>
                    <td className="muted">{formatDateTime(a.ReviewedOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {done.length === 0 ? (
        <section className="card">
          <div className="card-body">
            <p className="table-empty">No completed reviews yet.</p>
          </div>
        </section>
      ) : (
        <>
          {(summary?.comments.length ?? 0) > 0 ? (
            <section className="card">
              <div className="card-header">
                <h2 className="card-title">Reviewer comments</h2>
              </div>
              <div className="card-body">
                {summary?.comments.map((c, i) => (
                  <div className="review-block" key={`${c.ReviewerId}-${c.SectionKey}-${i}`}>
                    <h4 className="review-block-title">
                      {c.SectionName} ·{' '}
                      {summary.reviewers.find((r) => r.ReviewerId === c.ReviewerId)?.ReviewerName ??
                        `Reviewer ${c.ReviewerId}`}
                    </h4>
                    <p className="review-prose">{c.Comment}</p>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <section className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Answers side by side</h2>
                <p className="card-subtitle">
                  Every question on the IRB review form, as each reviewer answered it.
                </p>
              </div>
            </div>
            <div className="card-body">
              {sections.map((sectionName) => {
                const rows = (summary?.answers ?? []).filter((a) => a.SectionName === sectionName);
                const questions = Array.from(
                  new Map(rows.map((r) => [r.QuestionID, r])).values()
                ).sort((a, b) => a.SortOrder - b.SortOrder);

                return (
                  <div key={sectionName} style={{ marginBottom: 'var(--space-5)' }}>
                    <h3 className="review-block-title">{sectionName}</h3>
                    <div className="table-scroll">
                      <table className="data-table" style={{ minWidth: 720 }}>
                        <thead>
                          <tr>
                            <th scope="col" style={{ minWidth: 420 }}>
                              Question
                            </th>
                            {done.map((r) => (
                              <th scope="col" key={r.ReviewerId} className="cell-center">
                                {r.ReviewerName}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {questions.map((q) => (
                            <tr key={q.QuestionID}>
                              <td>{q.QuestionText}</td>
                              {done.map((r) => {
                                const answer = rows.find(
                                  (a) => a.QuestionID === q.QuestionID && a.ReviewerId === r.ReviewerId
                                );
                                return (
                                  <td key={r.ReviewerId} className="cell-center">
                                    {answer?.AnswerText ?? '—'}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
